import { useState } from 'react';
import { CheckCircle2, Pencil, RotateCcw, Trash2 } from 'lucide-react';
import type { ThreadDto } from '@aiai/shared';
import { formatRelative } from '../../lib/format';
import { personLabel, useViewerEmail } from '../../lib/people';
import { Badge } from '../Badge';
import { ConfirmDialog } from '../ConfirmDialog';
import { OverflowMenu } from '../OverflowMenu';
import { PageHeader } from '../PageHeader';
import { ThreadNotifyToggle } from '../ThreadNotifyToggle';

export const UNTITLED_THREAD = 'Untitled thread';

/**
 * The thread page's header: click-to-edit title (for posters), Open/Resolved
 * pill, meta line, Resolve/Reopen, and Delete behind the overflow menu. The
 * page owns the mutations; this only renders and confirms.
 */
export function ThreadHeader({
  thread,
  canEdit,
  busy,
  deleteError,
  onRename,
  onToggleResolved,
  onDelete,
}: {
  thread: ThreadDto;
  canEdit: boolean;
  busy: boolean;
  deleteError: string | null;
  onRename: (title: string) => void;
  onToggleResolved: () => void;
  onDelete: () => Promise<void>;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const viewerEmail = useViewerEmail();
  const resolved = thread.resolvedAt !== null;

  const meta = [
    thread.startedByEmail ? `started by ${personLabel(thread.startedByEmail, viewerEmail)}` : null,
    `${thread.messageCount} post${thread.messageCount === 1 ? '' : 's'}`,
    `${thread.participantCount} participant${thread.participantCount === 1 ? '' : 's'}`,
    `last activity ${formatRelative(thread.lastMessageAt)}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <PageHeader
        title={<EditableTitle value={thread.title} canEdit={canEdit && !busy} onSave={onRename} />}
        badges={
          <Badge tone={resolved ? 'neutral' : 'info'} dot={false}>
            {resolved ? 'Resolved' : 'Open'}
          </Badge>
        }
        subtitle={meta}
        actions={
          <>
            <ThreadNotifyToggle />
            {canEdit && (
              <button type="button" className="btn-secondary" disabled={busy} onClick={onToggleResolved}>
                {resolved ? (
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {resolved ? 'Reopen' : 'Resolve'}
              </button>
            )}
            {canEdit && (
              <OverflowMenu
                label="Thread actions"
                items={[
                  {
                    label: 'Delete',
                    icon: <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />,
                    onClick: () => setConfirmDelete(true),
                    danger: true,
                  },
                ]}
              />
            )}
          </>
        }
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete thread?"
        message={
          <>
            <span className="font-medium text-ink">“{thread.title || UNTITLED_THREAD}”</span>, all its
            posts and its discovered-issues ledger will be permanently deleted. Issues already filed
            from it stay on the board.
          </>
        }
        busy={busy}
        error={deleteError}
        onConfirm={() => void onDelete()}
        onClose={() => setConfirmDelete(false)}
      />
    </>
  );
}

function EditableTitle({
  value,
  canEdit,
  onSave,
}: {
  value: string | null;
  canEdit: boolean;
  onSave: (title: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const shown = value?.trim() || UNTITLED_THREAD;

  if (!canEdit) return <>{shown}</>;

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== (value ?? '')) onSave(next);
  };

  if (!editing) {
    return (
      <button
        type="button"
        className="group inline-flex max-w-full items-center gap-2 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60"
        aria-label="Rename thread"
        onClick={() => {
          setDraft(value ?? '');
          setEditing(true);
        }}
      >
        <span className="min-w-0 break-words">{shown}</span>
        <Pencil
          className="h-3.5 w-3.5 shrink-0 text-ink-faint opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          aria-hidden="true"
        />
      </button>
    );
  }

  return (
    <input
      autoFocus
      className="input font-display text-xl font-bold tracking-tight"
      aria-label="Thread title"
      value={draft}
      maxLength={200}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          setEditing(false);
        }
      }}
    />
  );
}
